import * as React from 'react';

import { Link, NavLink, useLocation } from 'react-router-dom';

import Card from 'antd/es/card';
import Tabs from 'antd/es/tabs';
import Tag from 'antd/es/tag';

import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { useMobileView } from '@common/react/hooks/useMobileView';

import { numberWithComma } from '@commonTuna/react/utils/NumberWithComma/NumberWithComma';

import { Doctor } from '@app/objects/Doctor';
import DoctorBookedTimeBlock from '@app/components/UI/DoctorBookedTimeBlock/DoctorBookedTimeBlock';
import { Location } from '@app/objects/Location';
import SpecialtyTag from '@app/components/Pages/Specialties/SpecialtyTag';
import DoctorAvatar from '@app/components/UI/DoctorCard/DoctorAvatar';
import Stars from '@app/components/UI/Stars/Stars';
import { getLocationAddress, getLocationName, showTime } from '@app/utils/Utils';
import Experience from '@app/components/UI/Experience/Experience';
import ShowPhone from '@app/components/UI/ShowPhone/ShowPhone';
import Rate from '@app/components/Pages/Doctor/Rate';

interface IProp {
	goToPath: (path: string, state) => void;
	from?: number;
	daysRange?: number;
	withoutBookedTime?: boolean;
	onClick?: () => void;
	selectLocation?: (location) => void;
	className?: string;
	selectLocationId?: number;
}

enum Days {
	'Sun',
	'Mon',
	'Tue',
	'Wed',
	'Thu',
	'Fri',
	'Sat',
}

const WorkingHours: React.FC<{ daysRange, workingHours?: Location['workingHours'], from: number}> = (props) => {
	const {
		workingHours,
		daysRange: propsDaysRange,
		from,
	} = props;
	const [currentFrom, setCurrentFrom] = React.useState(from);
	const isMobile = useMobileView(1100);
	const daysRange = isMobile ? 7 : propsDaysRange;
	React.useEffect(() => {
		if (from !== currentFrom) {
			setCurrentFrom(from);
		}
	}, [from, daysRange]);
	if (!workingHours?.length) return null;

	const firstWeekDay = new Date(currentFrom).getDay();
	const arr = Array.from({ length: daysRange || 0 }).map((_, i) => (firstWeekDay + i) % 7);

	return <div className="doctor-wh">
		<div className="doctor-wh-row show-mobile">
			<div>
				{daysRange < 7 && <button
					type="button"
					className="doctor-booked-time__button text-primary"
					onClick={() => setCurrentFrom((prev) => prev - 3600 * 24 * 1000 * daysRange)}
				>
					<i className="fa fa-chevron-left" />
				</button>}
			</div>
			{arr.map((i) => <div className="doctor-wh-cell text-primary" key={i}>
				{Days[i]}
			</div>)}
			<div>
				{daysRange < 7 && <button
					type="button"
					className="doctor-booked-time__button text-primary"
					onClick={() => setCurrentFrom((prev) => prev + 3600 * 24 * 1000 * daysRange)}
				>
					<i className="fa fa-chevron-right" />
				</button>}
			</div>
		</div>
		<div className="doctor-wh-row">
			<div>
				<i style={{ fontSize: 10 }}>Open:</i>
			</div>
			{arr.map((i) => <div className="doctor-wh-cell" key={i}>
				{showTime(workingHours?.find(({ dayOfWeek }) => dayOfWeek === i)?.startTime, false)}
			</div>)}
			{daysRange < 7 && <div className="show-mobile show-mobile__block" />}
		</div>
		<div className="doctor-wh-row">
			<div>
				<i style={{ fontSize: 10 }}>Closed:</i>
			</div>
			{arr.map((i) => <div className="doctor-wh-cell" key={i}>
				{showTime(workingHours?.find(({ dayOfWeek }) => dayOfWeek === i)?.endTime, false)}
			</div>)}
			{daysRange < 7 && <div className="show-mobile show-mobile__block" />}
		</div>
	</div>;
};

const DoctorCard: React.FC<Doctor & IProp> = (props) => {
	const {
		nameEn,
		specialties,
		locations,
		avatar,
		portalPathEn,
		id,
		averageReviewRate,
		totalReviewCount,
		portalShortText,
		// languages,
		doctorBookedTimes,
		from,
		daysRange,
		goToPath,
		withoutBookedTime,
		scheduleAppointment,
		onClick,
		selectLocation,
		className = '',
		selectLocationId,
		availableServices,
		experience,
		...rest
	} = props;

	const location = useLocation();

	const [selectedTime, setSelectedTime] = React.useState<number | undefined>();
	const [selectedLocation, setSelectedLocation] = React.useState<Location | undefined>(
		locations.find(({ locationId }) => locationId === getSearchParamsFromUrl(location)?.locationId)?.location
					|| locations?.[0]?.location,
	);

	React.useEffect(() => {
		if (selectedLocation?.id && selectedTime) {
			goToPath(`/doctors/${portalPathEn}`, { selectedLocation, scheduledTime: selectedTime });
		}
	}, [selectedTime, selectedLocation]);
	const rate = averageReviewRate || 0;

	const columnsCount = Object.keys(doctorBookedTimes?.values || {});
	const currentLocation = locations.find(({ locationId }) => locationId === selectLocationId)?.location;
	const locationWh = currentLocation
		? (currentLocation?.doctorWorkingHours ?? currentLocation.workingHours)
		: (locations[0]?.location?.doctorWorkingHours ?? locations[0]?.location?.workingHours);

	const title = React.useMemo(() => <>
		<div className="row">
			<div className={withoutBookedTime ? 'col-sm-12' : 'col-sm-4'}>
				<div className="card-content">
					<div className="card-title">
						<h3>{portalPathEn ? <Link to={`/doctors/${portalPathEn}`}>{nameEn}</Link> : nameEn}</h3>
					</div>
					<Experience experience={experience} />
					<Rate
						className="mb10"
						averageReviewRate={averageReviewRate}
						totalReviewCount={totalReviewCount}
					/>
					{(specialties && specialties.length > 0) || (availableServices?.length)
						? <h6 className="doctor-card-label">
							{specialties.map((s) => s.specialty && (
								<SpecialtyTag specialty={s.specialty} key={s.id} />
							))}
							{availableServices?.length ? availableServices?.filter((item) => item)
								.map((item) =>
									(<>
										{item.path
											? <NavLink key={`service${item.id}`} to={`/procedures/${item.path}`}>
												<Tag color="purple" className="title__second">
													{item.name}
												</Tag>
											</NavLink>
											: <Tag color="purple" key={`service${item.id}`} className="title__second">
												{item.name}
											</Tag>
										}
									</>)) : null}
						</h6> : null}
					{withoutBookedTime && locations && locations.length > 0 && <div
						className="mb10"
					>
						{locations.map(({ location }) => <span
							className="inline-select-item inline-select-item_sm"
							key={location.id}
							title={location.nameEn ? `${location.nameEn} ${location.addressEn || ''}` : ''}
						>
							{location.nameEn ? `${location.nameEn} ${location.addressEn || ''}` : ''}
						</span>)}
					</div>}
				</div>
				{portalShortText
					? <div className="ant-card-meta-description mb10" title={portalShortText} /> : null
				}
			</div>
			{!withoutBookedTime && <div className="col-sm-8 doctor-times">
				<div className="doctor-card-table">
					<div
						className={`doctor-card-locations ${locations.length > 1 ? 'doctor-card-locations-max' : ''}`}
					>
						{locations && locations.length > 0
							&& <Tabs
								type="card"
								activeKey={selectLocationId?.toString()}
								items={locations.map(({ location }) => ({
									key: location.id?.toString(),
									label: <>
										{currentLocation
											? null
											: !scheduleAppointment && location.id === locations[0].locationId
											&& locationWh && from && locations.length > 1 && <>
												<i className="fa fa-info-circle" title="Open/Close from current Location" />
												{' '}
											</>}
										{getLocationName(location, false, true)}
									</>,
									children: null,
								}))}
								onChange={(key) => {
									const location = locations.find(({ location }) => location.id === +key)?.location;
									location && selectLocation?.(location);
									location && setSelectedLocation(location);
								}}
							/>
						}
					</div>
					{currentLocation || selectedLocation ? <NavLink
						className="location-link"
						to={`/clinics/${(currentLocation || selectedLocation).portalPathEn}`}
					>
						<i className="fa fa-map-marker" />
						{' '}
						{getLocationAddress(currentLocation || selectedLocation)}
					</NavLink> : null}
					{!withoutBookedTime && doctorBookedTimes && scheduleAppointment && locations && locations.length > 0
						&& <DoctorBookedTimeBlock
							key={`${from} ${daysRange} ${columnsCount}`}
							value={doctorBookedTimes}
							doctorId={id}
							locations={locations.map((location) => location.location)}
							rowCount={5}
							days={daysRange}
							onSelect={(time, location, asNearestDate) => {
								goToPath(`/doctors/${portalPathEn}${asNearestDate
									? `?from=${time}` : ''}`, { selectedLocation: location, scheduledTime: time, asNearestDate });
							}}
							nearestDateRedirect
							className="text-center"
							withoutLocationButtons
							selectLocationById={selectedLocation?.id}
						/>}
					{!scheduleAppointment && <>
						{locationWh && from ? <WorkingHours
							workingHours={locationWh}
							from={from}
							daysRange={daysRange}
						/> : null}
						<div
							style={{
								flexGrow: 1,
							}}
							className="text-center mt10"
						>
							{(currentLocation || selectedLocation).showPhone && <ShowPhone
								className="btn btn-default"
								phone={(currentLocation || selectedLocation).phone}
								location={currentLocation || selectedLocation}
							/>}
							<button
								type="button"
								className="btn btn-default nearest-btn"
								onClick={() => {
									goToPath(`/doctors/${portalPathEn}`, { selectedLocationId: selectedLocation?.id });
								}}
							>
								Send Inquiry
							</button>
						</div>
					</>}
				</div>
			</div>}
		</div>
	</>, [nameEn, specialties, locations, portalPathEn, /* languages, */ doctorBookedTimes, selectedTime, selectedLocation, selectLocationId]);

	return (
		<div className={`doctor-card ${className}`}>
			<Card
				style={{ width: '100%' }}
				onClick={(e) => {
					if ((e.target as any)?.closest('a') || (e.target as any).tagName === 'A') {
						return undefined;
					}
					onClick && onClick();
				}}
			>
				<Card.Meta
					avatar={<DoctorAvatar
						alt={nameEn}
						src={avatar}
						path={portalPathEn}
					/>}
					title={title}
				/>
			</Card>
		</div>
	);
};

export default DoctorCard;
